import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// ── Kreatif Ağacı — editlenebilir accordion ─────────────────────────────────
// Üst satırlar (FEATURE) artık product_features tablosundan geliyor — burada
// feature ekle/sil/yeniden adlandır, pain point + audience listelerini
// düzenle yapılabiliyor (eskiden Ürün Ayarları'ndaki Feature Listesi'nin
// yerini alıyor). Her feature'ın altında gerçek üretim verisi (audience →
// message → execution) referans olarak gösterilir, read-only.

const ACCENT = "#5E6AD2";

function st(css: string): React.CSSProperties {
  const obj: Record<string, string> = {};
  css.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  });
  return obj as React.CSSProperties;
}

function H({
  tag: Tag = "div", baseStyle, hoverStyle, ...rest
}: { tag?: any; baseStyle: React.CSSProperties; hoverStyle?: React.CSSProperties; [k: string]: any }) {
  const [hover, setHover] = useState(false);
  return (
    <Tag
      {...rest}
      style={{ ...baseStyle, ...(hover ? hoverStyle : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    />
  );
}

type Execution = { code: string; angle: string | null; status: string; qa: { pass: boolean; decision: string } | null };
type Summary = { winners: number; losers: number; qa_fails: number; total: number };
type ApiTreeNode = {
  label: string;
  level: string;
  summary: Summary;
  children: ApiTreeNode[];
  executions: Execution[];
};
type Feature = { id: string; name: string; pain_points: string[]; audiences: string[] };

const EMPTY_SUMMARY: Summary = { winners: 0, losers: 0, qa_fails: 0, total: 0 };
const LEVEL_LABEL: Record<string, string> = { angle: "FEATURE", target_group: "AUDIENCE", message: "MESSAGE" };

export function CreativeTree({ productId }: { productId: string }) {
  const [tree, setTree] = useState<ApiTreeNode | null>(null);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [newFeatureName, setNewFeatureName] = useState("");
  const [addingFeature, setAddingFeature] = useState(false);
  const navigate = useNavigate();

  function reload() {
    setLoading(true);
    Promise.all([
      api.getIdeaTree(productId).catch(() => null),
      api.listFeatures(productId).catch(() => ({ features: [] })),
    ])
      .then(([t, f]) => { setTree(t?.tree ?? null); setFeatures(f.features ?? []); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { reload(); }, [productId]);

  async function addFeature() {
    const trimmed = newFeatureName.trim();
    if (!trimmed || features.some((f) => f.name === trimmed)) return;
    setAddingFeature(true);
    try {
      const r = await api.addFeature(productId, trimmed);
      setFeatures((prev) => [...prev, { id: r.id, name: trimmed, pain_points: [], audiences: [] }]);
      setNewFeatureName("");
    } finally {
      setAddingFeature(false);
    }
  }

  const producedByLabel = new Map((tree?.children ?? []).map((n) => [n.label, n]));
  const totals = tree?.summary ?? EMPTY_SUMMARY;

  return (
    <div style={st("flex:1;overflow-y:auto")}>
      <div style={st("max-width:820px;margin:0 auto;padding:40px 48px 64px")}>
        <div style={st("margin-bottom:22px")}>
          <h1 style={st("margin:0 0 6px;font-size:26px;font-weight:600;letter-spacing:-.02em")}>🌳 Kreatif Ağacı</h1>
          <p style={st("margin:0;font-size:14.5px;color:#6b6b76")}>Feature'ları buradan yönet — pain point/audience ekle, düzenle, sil. Altında gerçek üretim verisi görünür.</p>
        </div>

        {loading ? (
          <div style={st("padding:60px 0;text-align:center;color:#9a9aa2;font-size:13px")}>Yükleniyor…</div>
        ) : (
          <>
            {tree && tree.summary.total > 0 && (
              <div style={st("display:flex;gap:10px;margin-bottom:20px")}>
                <Stat label="Toplam" value={totals.total} color="#3a3a44" />
                <Stat label="🏆 Kazanan" value={totals.winners} color="#278a52" />
                <Stat label="Kaybeden" value={totals.losers} color="#c0392b" />
                <Stat label="⚠️ QA" value={totals.qa_fails} color="#c98a1a" />
              </div>
            )}

            <div style={st("display:flex;gap:8px;margin-bottom:14px")}>
              <input
                value={newFeatureName}
                onChange={(e) => setNewFeatureName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addFeature()}
                placeholder="Yeni feature adı (örn. ADHD, Alzheimer…)"
                style={st("flex:1;border:1px solid rgba(20,20,30,.12);border-radius:8px;padding:8px 12px;font-size:13px;font-family:inherit;outline:none")}
              />
              <H tag="button" onClick={addFeature}
                baseStyle={{ ...st("padding:8px 16px;border:none;border-radius:8px;color:#fff;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer"), background: ACCENT, opacity: addingFeature || !newFeatureName.trim() ? 0.5 : 1 }}
                hoverStyle={{ background: `color-mix(in srgb,${ACCENT} 85%,#000)` }}
              >
                + Ekle
              </H>
            </div>

            {features.length === 0 ? (
              <div style={st("padding:40px 0;text-align:center;color:#9a9aa2;font-size:13px;line-height:1.7")}>
                Henüz feature yok — yukarıdan bir tane ekle (örn. ADHD, Alzheimer, IQ Mix).
              </div>
            ) : (
              <div style={st("display:flex;flex-direction:column;gap:8px")}>
                {features.map((f) => (
                  <FeatureRow
                    key={f.id}
                    feature={f}
                    produced={producedByLabel.get(f.name) ?? null}
                    productId={productId}
                    navigate={navigate}
                    onDeleted={() => setFeatures((prev) => prev.filter((x) => x.id !== f.id))}
                    onUpdated={(patch) => setFeatures((prev) => prev.map((x) => (x.id === f.id ? { ...x, ...patch } : x)))}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:10px;padding:12px 15px;flex:1")}>
      <div style={{ ...st("font-size:20px;font-weight:600;letter-spacing:-.02em"), color }}>{value}</div>
      <div style={st("font-size:11px;color:#9a9aa2;margin-top:1px")}>{label}</div>
    </div>
  );
}

function Badges({ s }: { s: Summary }) {
  if (s.total === 0) return <span style={st("font-size:11.5px;color:#c2c2c8")}>henüz üretilmedi</span>;
  return (
    <div style={st("display:flex;gap:6px;align-items:center")}>
      <span style={st("font-size:11.5px;color:#9a9aa2")}>{s.total} üretildi</span>
      {s.winners > 0 && <Pill text={`🏆 ${s.winners}`} color="#278a52" />}
      {s.losers > 0 && <Pill text={`${s.losers} kaybetti`} color="#c0392b" />}
      {s.qa_fails > 0 && <Pill text={`⚠️ ${s.qa_fails}`} color="#c98a1a" />}
    </div>
  );
}

function Pill({ text, color }: { text: string; color: string }) {
  return (
    <span style={{ ...st("font-size:10.5px;font-weight:600;border-radius:5px;padding:2px 7px"), color, background: `color-mix(in srgb, ${color} 12%, #fff)` }}>
      {text}
    </span>
  );
}

function TagList({
  items, onAdd, onRemove, placeholder, tint,
}: {
  items: string[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  placeholder: string;
  tint: string;
}) {
  const [val, setVal] = useState("");
  return (
    <div>
      <div style={st("display:flex;gap:6px;margin-bottom:7px")}>
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && val.trim()) { onAdd(val.trim()); setVal(""); }
          }}
          placeholder={placeholder}
          style={st("flex:1;border:1px solid rgba(20,20,30,.1);border-radius:6px;padding:5px 9px;font-size:12px;font-family:inherit;outline:none")}
        />
        <H tag="button" onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}
          baseStyle={{ ...st("padding:5px 11px;border:none;border-radius:6px;font-family:inherit;font-size:12px;font-weight:500;cursor:pointer"), color: tint, background: `color-mix(in srgb, ${tint} 10%, #fff)` }}
          hoverStyle={{ background: `color-mix(in srgb, ${tint} 18%, #fff)` }}
        >+</H>
      </div>
      <div style={st("display:flex;flex-wrap:wrap;gap:5px")}>
        {items.map((v) => (
          <span key={v} style={{ ...st("display:inline-flex;align-items:center;gap:5px;font-size:11.5px;border-radius:5px;padding:3px 8px"), color: tint, background: `color-mix(in srgb, ${tint} 9%, #fff)` }}>
            {v}
            <button onClick={() => onRemove(v)} style={st("border:none;background:transparent;cursor:pointer;color:inherit;opacity:.6;line-height:1;padding:0;font-size:11px")}>×</button>
          </span>
        ))}
        {items.length === 0 && <span style={st("font-size:11.5px;color:#c2c2c8")}>Henüz yok</span>}
      </div>
    </div>
  );
}

function FeatureRow({
  feature, produced, productId, navigate, onDeleted, onUpdated,
}: {
  feature: Feature;
  produced: ApiTreeNode | null;
  productId: string;
  navigate: ReturnType<typeof useNavigate>;
  onDeleted: () => void;
  onUpdated: (patch: Partial<Feature>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(feature.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function saveName() {
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (!trimmed || trimmed === feature.name) { setNameDraft(feature.name); return; }
    onUpdated({ name: trimmed });
    await api.updateFeature(productId, feature.id, { name: trimmed }).catch(() => onUpdated({ name: feature.name }));
  }

  async function updatePainPoints(next: string[]) {
    onUpdated({ pain_points: next });
    await api.updateFeature(productId, feature.id, { pain_points: next });
  }
  async function updateAudiences(next: string[]) {
    onUpdated({ audiences: next });
    await api.updateFeature(productId, feature.id, { audiences: next });
  }
  async function doDelete() {
    await api.deleteFeature(productId, feature.id);
    onDeleted();
  }

  return (
    <div style={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:11px;overflow:hidden")}>
      <div style={st("display:flex;align-items:center;gap:10px;padding:14px 16px")}>
        <H onClick={() => setOpen((o) => !o)}
          baseStyle={st("display:flex;align-items:center;gap:10px;flex:1;min-width:0;cursor:pointer")}
        >
          <span style={{ ...st("font-size:11px;transition:transform .15s;color:#9a9aa2"), transform: open ? "rotate(90deg)" : "none" }}>▶</span>
          <span style={{ ...st("font-size:10.5px;font-weight:700;letter-spacing:.04em;border-radius:5px;padding:2px 7px;flex:none"), color: ACCENT, background: `color-mix(in srgb, ${ACCENT} 10%, #fff)` }}>
            FEATURE
          </span>
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setNameDraft(feature.name); setEditingName(false); } }}
              onBlur={saveName}
              style={st("font-size:14px;font-weight:600;border:1px solid rgba(20,20,30,.15);border-radius:6px;padding:2px 7px;font-family:inherit;outline:none")}
            />
          ) : (
            <span
              onClick={(e) => { e.stopPropagation(); setEditingName(true); }}
              style={st("font-size:14px;font-weight:600;cursor:text")}
              title="Yeniden adlandırmak için tıkla"
            >
              {feature.name}
            </span>
          )}
        </H>
        <Badges s={produced?.summary ?? EMPTY_SUMMARY} />
        {confirmDelete ? (
          <span style={st("display:flex;gap:6px;align-items:center;flex:none")}>
            <span style={st("font-size:11.5px;color:#c0392b")}>Emin misin?</span>
            <H tag="button" onClick={doDelete}
              baseStyle={st("border:none;background:#c0392b;color:#fff;border-radius:6px;padding:3px 9px;font-size:11.5px;font-family:inherit;cursor:pointer")}
              hoverStyle={st("background:#a53025")}
            >Sil</H>
            <H tag="button" onClick={() => setConfirmDelete(false)}
              baseStyle={st("border:none;background:transparent;color:#9a9aa2;border-radius:6px;padding:3px 9px;font-size:11.5px;font-family:inherit;cursor:pointer")}
              hoverStyle={st("background:rgba(20,20,30,.05)")}
            >Vazgeç</H>
          </span>
        ) : (
          <H tag="button" onClick={() => setConfirmDelete(true)}
            baseStyle={st("border:none;background:transparent;color:#c2c2c8;border-radius:6px;padding:3px 7px;font-size:12px;font-family:inherit;cursor:pointer;flex:none")}
            hoverStyle={st("background:rgba(192,57,43,.08);color:#c0392b")}
          >🗑</H>
        )}
      </div>

      {open && (
        <div style={st("padding:2px 16px 16px 34px;display:flex;flex-direction:column;gap:14px")}>
          <div style={st("display:grid;grid-template-columns:1fr 1fr;gap:16px")}>
            <div>
              <p style={st("font-size:11px;font-weight:700;letter-spacing:.03em;color:#8a8a93;margin:0 0 6px")}>PAIN POINTS</p>
              <TagList
                items={feature.pain_points}
                placeholder="Ekle…"
                tint="#c98a1a"
                onAdd={(v) => updatePainPoints([...feature.pain_points, v])}
                onRemove={(v) => updatePainPoints(feature.pain_points.filter((x) => x !== v))}
              />
            </div>
            <div>
              <p style={st("font-size:11px;font-weight:700;letter-spacing:.03em;color:#8a8a93;margin:0 0 6px")}>AUDIENCES</p>
              <TagList
                items={feature.audiences}
                placeholder="Ekle…"
                tint="#7c5cd6"
                onAdd={(v) => updateAudiences([...feature.audiences, v])}
                onRemove={(v) => updateAudiences(feature.audiences.filter((x) => x !== v))}
              />
            </div>
          </div>

          {produced && produced.children.length > 0 && (
            <div>
              <p style={st("font-size:11px;font-weight:700;letter-spacing:.03em;color:#8a8a93;margin:0 0 6px")}>GERÇEK ÜRETİM</p>
              <div style={st("display:flex;flex-direction:column;gap:6px")}>
                {produced.children.map((child, i) => (
                  <ChildRow key={i} node={child} />
                ))}
              </div>
            </div>
          )}

          <H tag="button" onClick={() => navigate(`/products/${productId}/studio?prefill=${encodeURIComponent(`${feature.name} için yeni bir varyasyon üret`)}`)}
            baseStyle={{ ...st("align-self:flex-start;padding:6px 12px;border-radius:7px;font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;border:1px solid transparent"), color: ACCENT, background: `color-mix(in srgb, ${ACCENT} 8%, #fff)` }}
            hoverStyle={{ background: `color-mix(in srgb, ${ACCENT} 14%, #fff)` }}
          >
            + Bu feature için üret
          </H>
        </div>
      )}
    </div>
  );
}

function ChildRow({ node }: { node: ApiTreeNode }) {
  const [open, setOpen] = useState(false);
  const hasChildren = node.children.length > 0;
  const hasExecutions = node.executions.length > 0;
  const expandable = hasChildren || hasExecutions;

  return (
    <div style={st("border:1px solid rgba(20,20,30,.06);border-radius:9px;overflow:hidden;background:#fbfbfc")}>
      <H onClick={() => expandable && setOpen((o) => !o)}
        baseStyle={{ ...st("display:flex;align-items:center;gap:9px;padding:9px 12px"), cursor: expandable ? "pointer" : "default" }}
        hoverStyle={expandable ? st("background:rgba(20,20,30,.03)") : undefined}
      >
        {expandable ? (
          <span style={{ ...st("font-size:10px;color:#9a9aa2;transition:transform .15s"), transform: open ? "rotate(90deg)" : "none" }}>▶</span>
        ) : <span style={st("width:10px")} />}
        <span style={{ ...st("font-size:9.5px;font-weight:700;letter-spacing:.03em;border-radius:4px;padding:1.5px 6px;color:#7a7a86;background:rgba(20,20,30,.05)") }}>
          {LEVEL_LABEL[node.level] ?? node.level.toUpperCase()}
        </span>
        <span style={st("font-size:13px;font-weight:500")}>{node.label}</span>
        <span style={st("margin-left:auto")}><Badges s={node.summary} /></span>
      </H>
      {open && (
        <div style={st("padding:0 12px 10px 30px;display:flex;flex-direction:column;gap:5px")}>
          {node.children.map((child, i) => (
            <ChildRow key={i} node={child} />
          ))}
          {hasExecutions && (
            <div style={st("display:flex;flex-wrap:wrap;gap:6px;margin-top:2px")}>
              {node.executions.map((ex) => {
                const color = ex.status === "winner" ? "#278a52" : ex.status === "loser" ? "#c0392b" : "#8a8a93";
                return (
                  <span key={ex.code} title={ex.angle ?? undefined}
                    style={{ ...st("font-size:10.5px;font-family:monospace;border-radius:5px;padding:3px 8px;border:1px solid"), color, borderColor: `color-mix(in srgb, ${color} 30%, transparent)`, background: `color-mix(in srgb, ${color} 7%, #fff)` }}>
                    {ex.status === "winner" ? "🏆 " : ex.qa && !ex.qa.pass ? "⚠️ " : ""}{ex.code}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
